import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Instagram, Star, ExternalLink, QrCode, FileText, CreditCard, Dumbbell, Smartphone, Salad, MessageCircle, TrendingUp, PlayCircle, Zap, Clock4, Flame } from "lucide-react";
import { BRAND, WHATSAPP_LINK, MEDIA, ONLINE_PLANS, ONLINE_BENEFITS, HOW_IT_WORKS, GOOGLE_REVIEWS, FAQ_ONLINE } from "@/lib/data";
import SectionTitle from "@/components/SectionTitle";
import Reveal from "@/components/Reveal";
import BeforeAfter from "@/components/BeforeAfter";
import FAQ from "@/components/FAQ";

const iconMap = { Dumbbell, Smartphone, Salad, MessageCircle, TrendingUp, PlayCircle };

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < n ? "text-yellow-400 fill-yellow-400" : "text-neutral-600"}`}
        />
      ))}
    </div>
  );
}

export default function OnlineConsulting() {
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={MEDIA.trainerPortrait}
            alt=""
            className="absolute inset-0 w-full h-full object-cover animate-[kenburns_22s_ease-in-out_infinite_alternate]"
            style={{ filter: "saturate(1.05) contrast(1.05)", objectPosition: "center 25%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black" />
          <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-orange-500/15 blur-[140px]" />
          <div className="absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t from-black to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-36 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-yellow-400/30 bg-yellow-400/5 px-3 py-1 text-[10px] sm:text-xs font-bold tracking-[0.22em] uppercase text-yellow-400">
              Online · Do seu jeito · No seu tempo
            </span>
            <h1 className="mt-5 font-heading font-black tracking-[-0.03em] text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.92] text-white">
              Consultoria <span className="text-gradient-yo">Online</span>
            </h1>
            <p className="mt-5 text-neutral-300 text-base sm:text-lg max-w-xl leading-relaxed">
              Transformação de onde você estiver. Método científico, suporte direto comigo e resultado garantido — com app MFIT no seu bolso.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#investimento"
                data-testid="online-hero-cta-plans"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-6 py-3.5 text-sm uppercase tracking-wider shadow-[0_0_30px_rgba(250,204,21,0.35)] hover:shadow-[0_0_50px_rgba(250,204,21,0.55)] hover:-translate-y-0.5 transition-all"
              >
                Ver planos <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={WHATSAPP_LINK("Olá Vagner! Tenho interesse na consultoria online.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="online-hero-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur text-white font-semibold px-6 py-3.5 text-sm uppercase tracking-wider hover:bg-white/10 hover:border-yellow-400/40 transition-all"
              >
                Falar comigo
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRAINER STRIP */}
      <section className="relative py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5]">
              <img
                src={MEDIA.trainerPortrait}
                alt="Vagner Milioli"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <SectionTitle
              overline="Seu Treinador"
              align="left"
              title={<>Vagner <span className="text-gradient-yo">Milioli</span></>}
            />
            <div className="mt-6 space-y-4 text-neutral-300 text-base leading-relaxed">
              <p>
                Graduado em Educação Física e Personal Trainer, meu foco é unir o que há de mais atual na ciência do exercício à realidade da sua rotina. Acredito que a constância sempre vence a motivação e, por isso, foco em ajudar você a integrar o movimento na sua vida de forma inteligente e prazerosa.
              </p>
              <p className="text-neutral-400">
                Meu trabalho aqui é guiar você pelo caminho mais seguro e eficiente para alcançar sua melhor versão, com um acompanhamento profissional de verdade.
              </p>
            </div>
            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="online-about-instagram"
              className="mt-6 inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm font-semibold"
            >
              <Instagram className="w-4 h-4" /> @{BRAND.instagram}
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionTitle
            overline="O que você recebe"
            title={<>Tudo para <span className="text-gradient-yo">destravar resultado</span></>}
            description="Treino, suporte e método unidos em um ecossistema prático."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ONLINE_BENEFITS.map((b, i) => {
              const Icon = iconMap[b.icon] || CheckCircle2;
              return (
                <Reveal
                  key={b.title}
                  delay={i * 0.06}
                  className="relative glass-card rounded-2xl p-6 sm:p-7 overflow-hidden hover:border-yellow-400/40 hover:-translate-y-1 transition-all duration-300"
                  data-testid={`online-benefit-${i}`}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-black">
                    <Icon className="w-6 h-6" strokeWidth={2.4} />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-bold text-white">{b.title}</h3>
                  <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{b.description}</p>
                  {b.link ? (
                    <a
                      href={b.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-yellow-400 hover:text-yellow-300"
                    >
                      {b.link.label} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : null}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-20 sm:py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[300px] rounded-full bg-orange-500/10 blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <SectionTitle
            overline="Como funciona"
            title={<>Processo simples, <span className="text-gradient-yo">resultados reais</span></>}
            description="Da inscrição à transformação — sem complicação."
          />
          <div className="mt-14 grid md:grid-cols-3 gap-5 relative">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal
                key={s.n}
                delay={i * 0.1}
                className="relative glass-card rounded-3xl p-7"
                data-testid={`online-step-${i}`}
              >
                <div className="font-heading text-5xl font-black text-gradient-yo">{s.n}</div>
                <h3 className="mt-3 font-heading text-xl font-bold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{s.description}</p>
                {i < HOW_IT_WORKS.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-4 text-yellow-400/40 w-6 h-6" />
                )}
              </Reveal>
            ))}
          </div>

          {/* Timeline post-payment */}
          <Reveal className="mt-14 rounded-3xl border border-white/10 bg-neutral-900/40 backdrop-blur p-7 sm:p-10">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-yellow-400" />
              <h4 className="font-heading text-xl sm:text-2xl font-bold text-white">
                Após a confirmação do pagamento
              </h4>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/5 bg-black/40 p-5 flex items-start gap-3" data-testid="online-timeline-24h">
                <Clock4 className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-yellow-400 font-bold">Em até 24h</div>
                  <p className="mt-1 text-sm text-neutral-300">Você recebe a anamnese para preenchimento.</p>
                </div>
              </div>
              <div className="rounded-2xl border border-white/5 bg-black/40 p-5 flex items-start gap-3" data-testid="online-timeline-72h">
                <Clock4 className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-yellow-400 font-bold">Em até 72h</div>
                  <p className="mt-1 text-sm text-neutral-300">Protocolo completo entregue após o preenchimento da anamnese.</p>
                </div>
              </div>
            </div>
            <p className="mt-6 text-center text-sm text-neutral-400">
              Comece sua transformação hoje mesmo.
            </p>
          </Reveal>
        </div>
      </section>

      {/* RESULTS */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionTitle
            overline="Antes e Depois"
            title={<>Resultados de <span className="text-gradient-yo">alunos</span></>}
            description="Arraste para revelar a transformação. Resultados são fruto de protocolo + consistência."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 max-w-5xl mx-auto">
            <BeforeAfter
              before={MEDIA.beforeAfterMan2Before}
              after={MEDIA.beforeAfterMan2After}
              alt="Vagner Milioli — transformação online"
              testid="online-before-after-1"
            />
            <BeforeAfter
              before={MEDIA.beforeAfterClientBefore}
              after={MEDIA.beforeAfterClientAfter}
              alt="Cliente — transformação online"
              testid="online-before-after-2"
            />
          </div>
          <Reveal className="mt-10 flex justify-center" delay={0.15}>
            <a
              href={WHATSAPP_LINK("Olá Vagner! Quero ser o próximo aluno online a transformar.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="online-next-transformation-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-6 py-3.5 text-sm uppercase tracking-widest hover:-translate-y-0.5 shadow-[0_0_30px_rgba(250,204,21,0.35)] transition-all"
            >
              <Flame className="w-4 h-4" /> Quero ser o próximo <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>
      <section id="investimento" className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionTitle
            overline="Investimento"
            title={<>Escolha seu <span className="text-gradient-yo">plano</span></>}
            description="O próximo passo para a sua evolução começa aqui."
          />
          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {ONLINE_PLANS.map((p, i) => (
              <Reveal
                key={p.id}
                delay={i * 0.06}
                className={`relative rounded-3xl p-6 sm:p-7 flex flex-col ${
                  p.badge
                    ? "bg-gradient-to-br from-yellow-400/10 via-neutral-900 to-orange-500/10 border border-yellow-400/50 shadow-glow-yellow xl:scale-[1.02]"
                    : "glass-card hover:border-yellow-400/30"
                } transition-all`}
                data-testid={`online-plan-${p.id}`}
              >
                {p.badge ? (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-[10px] font-black uppercase tracking-widest px-3 py-1">
                    {p.badge}
                  </span>
                ) : null}
                <div className="text-[10px] uppercase tracking-[0.22em] text-yellow-400 font-bold">Plano</div>
                <h3 className="mt-2 font-heading text-3xl font-black text-white">{p.name}</h3>
                <div className="mt-1 text-xs text-neutral-400">{p.period}</div>

                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-heading text-3xl font-black text-white">{p.price}</span>
                  <span className="text-xs text-neutral-500">{p.priceSuffix}</span>
                </div>
                <div className="mt-1 text-sm">
                  <span className="text-gradient-yo font-bold">{p.pix}</span>{" "}
                  <span className="text-neutral-500 text-xs">{p.pixNote}</span>
                </div>

                <div className="mt-5 h-px bg-white/5" />

                <ul className="mt-5 space-y-2.5 text-sm text-neutral-300 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-yellow-400 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 flex flex-col gap-2">
                  <a
                    href={p.paymentLinks?.pix || WHATSAPP_LINK(
                      `Olá Vagner! Quero fechar o plano ${p.name} (${p.period}) no PIX/Boleto.`
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`online-plan-cta-pix-${p.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-5 py-3 text-xs uppercase tracking-widest hover:-translate-y-0.5 shadow-[0_0_20px_rgba(250,204,21,0.3)] transition-all"
                  >
                    <QrCode className="w-4 h-4" /> PIX / Boleto
                  </a>
                  <a
                    href={p.paymentLinks?.card || WHATSAPP_LINK(
                      `Olá Vagner! Quero fechar o plano ${p.name} (${p.period}) no Cartão de Crédito.`
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`online-plan-cta-card-${p.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 text-white font-bold px-5 py-3 text-xs uppercase tracking-widest hover:bg-white/10 hover:border-yellow-400/40 transition-all"
                  >
                    <CreditCard className="w-4 h-4" /> Cartão de Crédito
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Payment methods grid */}
          <div className="mt-14 grid sm:grid-cols-3 gap-4">
            {[
              { icon: QrCode, title: "PIX", desc: "10% de desconto à vista", testid: "online-payment-pix" },
              { icon: FileText, title: "Boleto Bancário", desc: "10% de desconto à vista", testid: "online-payment-boleto" },
              { icon: CreditCard, title: "Cartão de Crédito", desc: "Parcele em até 3x sem juros", testid: "online-payment-card" },
            ].map((m, i) => (
              <Reveal key={m.title} delay={i * 0.06} className="glass-card rounded-2xl p-6 flex items-start gap-4" data-testid={m.testid}>
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-black shrink-0">
                  <m.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold text-white">{m.title}</div>
                  <p className="mt-0.5 text-sm text-neutral-400">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col items-center text-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-yellow-400/30 bg-yellow-400/5 px-3 py-1 text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-yellow-400">
              Avaliações do Google
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              O que meus <span className="text-gradient-yo">alunos dizem</span>
            </h2>
            <div className="mt-2 flex items-center gap-3">
              <span className="font-heading text-3xl font-black text-white">5.0</span>
              <Stars n={5} />
              <span className="text-sm text-neutral-400">+10 avaliações</span>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {GOOGLE_REVIEWS.map((r, i) => (
              <Reveal
                key={r.name + i}
                delay={i * 0.05}
                className="glass-card rounded-2xl p-6 flex flex-col"
                data-testid={`online-review-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 text-black font-heading font-black grid place-items-center">
                      {r.name.trim().charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-heading font-bold text-white text-sm">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">via Google</div>
                    </div>
                  </div>
                  <svg viewBox="0 0 48 48" className="w-6 h-6" aria-hidden>
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.1 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.3 1 7.3 2.7l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 18.9 13 24 13c2.8 0 5.3 1 7.3 2.7l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.5 6.3 14.7z"/>
                    <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-5c-1.9 1.3-4.3 2-6.9 2-5.3 0-9.9-3.4-11.5-8.1l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4-4.3 5.2l6 5c-.4.3 6.4-4.7 6.4-14.2 0-1.2-.1-2.3-.4-3.5z"/>
                  </svg>
                </div>
                <div className="mt-4">
                  <Stars n={r.rating} />
                </div>
                <p className="mt-3 text-sm text-neutral-300 leading-relaxed flex-1">"{r.text}"</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href={BRAND.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="online-google-see"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 text-white font-bold px-5 py-3 text-xs uppercase tracking-widest hover:bg-white/10 hover:border-yellow-400/40 transition-all"
            >
              Ver no Google <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href={BRAND.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="online-google-review"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-5 py-3 text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-all"
            >
              Avaliar no Google <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <FAQ items={FAQ_ONLINE} title="Perguntas frequentes sobre a consultoria online" testid="online-faq" />

      {/* Final CTA */}
      <section className="relative py-20 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal className="relative overflow-hidden rounded-3xl p-8 sm:p-12 bg-gradient-to-br from-yellow-400 to-orange-500 text-black">
            <div className="absolute inset-0 noise opacity-40" />
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
                  Comece sua transformação hoje.
                </h3>
                <p className="mt-3 text-sm sm:text-base text-black/80 max-w-md">
                  ⚡ VAGAS LIMITADAS • ESTRATÉGIA INDIVIDUALIZADA • EVOLUÇÃO DE VERDADE • RESULTADADOS REAIS E CONSISTENTES
                </p>
              </div>
              <div className="md:text-right">
                <a
                  href={WHATSAPP_LINK("Olá Vagner! Quero começar a consultoria online.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="online-final-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-black text-white font-bold px-6 py-3.5 text-sm uppercase tracking-widest hover:bg-neutral-800 transition-colors"
                >
                  Quero minha vaga <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
